import React, { useContext, useMemo } from 'react';
import { BreadcrumbGroup, Spinner } from '@cloudscape-design/components';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AccountContext } from 'src/context/accountContext';

export const Breadcrumbs: React.FC = () => {
  const { selectedAccount, loading } = useContext(AccountContext);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    applicationId,
    modelVersionId,
    operatorFlowId,
    modelArtifactId,
    modelRegistryId,
    variationId,
  } = useParams();

  const pigeonBreadcrumbItems = useMemo(() => {
    const items = [
      {
        text: 'Applications',
        href: `/accounts/${selectedAccount?.id}`,
      },
    ];

    if (applicationId) {
      items.push({
        text: `Application (...${applicationId?.slice(-5)})`,
        href: `/accounts/${selectedAccount?.id}/applications/${applicationId}`,
      });
    }

    if (modelVersionId) {
      items.push({
        text: `Model Version (...${modelVersionId?.slice(-5)})`,
        href: `/accounts/${selectedAccount?.id}/applications/${applicationId}/modelVersions/${modelVersionId}`,
      });
    }

    if (modelArtifactId && location.pathname.includes('operatorFlowJobs')) {
      items.push({
        text: `Operator Flows (...${modelArtifactId?.slice(-5)})`,
        href: `/accounts/${selectedAccount?.id}/applications/${applicationId}/modelVersions/${modelVersionId}/modelArtifacts/${modelArtifactId}/operatorFlowJobs`,
      });
    }

    if (operatorFlowId) {
      items.push({
        text: `Operator Flow (...${operatorFlowId?.slice(-5)})`,
        href: `/accounts/${selectedAccount?.id}/applications/${applicationId}/operatorFlowJobs/${operatorFlowId}`,
      });
    }

    if (location.pathname.endsWith('monitoring')) {
      items.push({
        text: `Model Metrics (...${modelArtifactId?.slice(-5)})`,
        href: '',
      });
    }

    if (location.pathname.endsWith('dataQualityMetrics')) {
      items.push({
        text: `Data Quality Metrics (...${modelArtifactId?.slice(-5)})`,
        href: '',
      });
    }

    return items;
  }, [loading, applicationId, modelVersionId, operatorFlowId, modelArtifactId]);

  const modelRegistryBreadcrumbItems = useMemo(() => {
    const items = [
      {
        text: 'Browse Models',
        href: `/modelRegistry`,
      },
    ];

    if (modelRegistryId) {
      items.push({
        text: `Model (...${modelRegistryId?.slice(-5)})`,
        href: `/modelRegistry/model/${modelRegistryId}`,
      });
    }

    if (variationId) {
      items.push({
        text: `Model Variation (...${variationId?.slice(-5)})`,
        href: `/modelRegistry/model/${modelRegistryId}/variation/${variationId}`,
      });
    }

    return items;
  }, [loading, modelRegistryId, variationId]);

  if (loading) return <Spinner />;

  if (!modelRegistryId && !applicationId) return <></>;

  return (
    <BreadcrumbGroup
      items={
        location.pathname.includes('modelRegistry')
          ? modelRegistryBreadcrumbItems
          : pigeonBreadcrumbItems
      }
      ariaLabel="Breadcrumbs"
      onFollow={(e) => {
        e.preventDefault();
        navigate(e.detail.href);
      }}
    />
  );
};

export default Breadcrumbs;
